import axios from 'axios';

export const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('le_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global 402 interceptor: surface plan/quota errors via the QuotaExceededDialog.
// We dynamically import to avoid a circular dependency with lib/billing.js.
// Callers that handle their own 402 inline (e.g. the wizard's persist-and-resume
// flow) should pass { _skipQuotaDialog: true } on the request config.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 402) {
      const skip = error.config?._skipQuotaDialog === true;
      const detail = error.response.data?.detail;
      if (!skip && detail && typeof detail === 'object') {
        import('./billing').then((mod) => mod.dispatchQuotaExceeded(detail)).catch(() => {});
      }
    }
    return Promise.reject(error);
  },
);

export const auth = {
  signup: (payload) => api.post('/auth/signup', payload).then((r) => r.data),
  login: (payload) => api.post('/auth/login', payload).then((r) => r.data),
  me: () => api.get('/auth/me').then((r) => r.data),
};

export const linkedge = {
  scrape: (url) => api.post('/scrape', { url }).then((r) => r.data),
  importPdf: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api
      .post('/import-pdf', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
  getImportPdfJob: (jobId) => api.get(`/import-pdf/${jobId}`).then((r) => r.data),
  analyze: (payload) => api.post('/analyze', payload, { _skipQuotaDialog: true }).then((r) => r.data),
  getAnalysis: (id) => api.get(`/analyze/${id}`).then((r) => r.data),
  regeneratePosts: (id, payload) => api.post(`/analyze/${id}/regenerate-posts`, payload || {}).then((r) => r.data),
  regenerateOnePost: (id, payload) => api.post(`/analyze/${id}/posts/regenerate-one`, payload).then((r) => r.data),
  regenerateAbout: (id, payload) => api.post(`/analyze/${id}/regenerate-about`, payload || {}).then((r) => r.data),
  rebuildBlueprint: async (id, opts = {}) => {
    const { data } = await api.post(`/analyze/${id}/rebuild-blueprint`);
    if (data?.skipped) return data;                // already had content
    // ``skipPolling: true`` returns the raw kickoff response
    // (``{ job_id, est_seconds_total, ... }``) so a caller can drive its
    // own polling — used by ``RebuildAuditCard`` to render a live
    // countdown timer + stage strip instead of a static toast.
    if (opts.skipPolling) return data;
    // Fast-tier engine finishes in ~3 minutes; safety-poll for 8 min.
    return await linkedge.pollJob(data.job_id, { intervalMs: 3000, maxMs: 480000 });
  },
  regenerateTone: (id, slider) => api.post(`/analyze/${id}/regenerate-tone`, { slider }).then((r) => r.data),
  regenerateToneStatus: (id) => api.get(`/analyze/${id}/regenerate-tone/status`).then((r) => r.data),
  regenerateLens: (id, lens) => api.post(`/analyze/${id}/regenerate-lens`, { lens }).then((r) => r.data),
  regenerateLensStatus: (id) => api.get(`/analyze/${id}/regenerate-lens/status`).then((r) => r.data),
  regenerateDimension: (id, dimension_key) => api.post(`/analyze/${id}/regenerate-dimension`, { dimension_key }).then((r) => r.data),
  rewriteSection: (id, payload) => api.post(`/analyze/${id}/rewrite-section`, payload).then((r) => r.data),
  prepareContent: (id) => api.post(`/analyze/${id}/prepare-content`).then((r) => r.data),
  refreshInspiration: (id, payload) => api.post(`/analyze/${id}/refresh-inspiration`, payload || {}).then((r) => r.data),
  refreshResearch: (id) => api.post(`/analyze/${id}/refresh-research`, {}).then((r) => r.data),
  createCalendar: (id, payload) => api.post(`/analyze/${id}/calendar`, payload).then((r) => r.data),
  downloadCalendar: async (id, fmt) => {
    const token = localStorage.getItem('le_token');
    const res = await fetch(`${API_BASE}/analyze/${id}/calendar/export/${fmt}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Calendar download failed');
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `execpresenceai-calendar.${fmt}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  history: () => api.get('/history').then((r) => r.data),
  deleteAnalysis: (id) => api.delete(`/analyze/${id}`).then((r) => r.data),
  exportUrl: (id, format) => `${API_BASE}/analyze/${id}/export/${format}`,

  // Audit timeline — score trajectory for a profile name across multiple audits.
  auditTimeline: (profile_name, limit) =>
    api
      .get('/analyze/timeline', { params: { profile_name, limit } })
      .then((r) => r.data),

  // Generic async job polling
  pollJob: async (jobId, { intervalMs = 2500, maxMs = 240000 } = {}) => {
    const t0 = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { data } = await api.get(`/jobs/${jobId}`);
      if (data?.status === 'complete') return data.result;
      if (data?.status === 'error') throw new Error(data.error || 'Job failed');
      if (Date.now() - t0 > maxMs) throw new Error('Job timed out');
      await new Promise((r) => setTimeout(r, intervalMs));
    }
  },

  // Comment feed (real posts to comment on) — async-by-default
  generateCommentFeed: async (id, payload) => {
    const { data } = await api.post(`/analyze/${id}/comment-feed/generate`, payload || {}, { timeout: 30000 });
    return await linkedge.pollJob(data.job_id);
  },
  refreshOneComment: async (id, itemId) => {
    const { data } = await api.post(
      `/analyze/${id}/comment-feed/refresh-one`,
      { item_id: itemId },
      { timeout: 30000 },
    );
    return await linkedge.pollJob(data.job_id);
  },
  refreshAllComments: async (id, payload) => {
    const { data } = await api.post(`/analyze/${id}/comment-feed/refresh-all`, payload || {}, { timeout: 30000 });
    return await linkedge.pollJob(data.job_id);
  },

  // PoV
  povNewsList: (id) => api.get(`/analyze/${id}/pov/news-list`, { timeout: 90000 }).then((r) => r.data),
  povGenerate: async (id, payload) => {
    const { data } = await api.post(`/analyze/${id}/pov/generate`, payload, { timeout: 30000 });
    return await linkedge.pollJob(data.job_id);
  },

  // Carousel
  getCarousel: (id, idx) =>
    api.get(`/analyze/${id}/posts/${idx}/carousel`, { timeout: 90000 }).then((r) => r.data),
  regenerateCarousel: async (id, idx) => {
    const { data } = await api.post(`/analyze/${id}/posts/${idx}/carousel/regenerate`, {}, { timeout: 30000 });
    return await linkedge.pollJob(data.job_id);
  },
  saveCarousel: (id, idx, payload) =>
    api.post(`/analyze/${id}/posts/${idx}/carousel/save`, payload, { timeout: 60000 }).then((r) => r.data),

  // Post context override (industry / function / audience for sharper post generation)
  updatePostContext: (id, payload) =>
    api.patch(`/analyze/${id}/post-context`, payload, { timeout: 30000 }).then((r) => r.data),

  // Voice profile override — owner OR admin can edit. Merged server-side.
  // Next post regeneration picks up the new voice automatically.
  updateVoiceProfile: (id, payload) =>
    api.patch(`/analyze/${id}/voice-profile`, payload, { timeout: 30000 }).then((r) => r.data),

  // Edit a single generated post in place (owner OR admin)
  updatePost: (id, postIndex, payload) =>
    api.patch(`/analyze/${id}/posts/${postIndex}`, payload, { timeout: 30000 }).then((r) => r.data),

  // Remove a single generated post (owner OR admin)
  deletePost: (id, postIndex) =>
    api.delete(`/analyze/${id}/posts/${postIndex}`, { timeout: 30000 }).then((r) => r.data),

  // Manus-generated posts (test tab)
  generateManusPosts: async (id, payload) => {
    const { data } = await api.post(`/analyze/${id}/manus-posts/generate`, payload || {});
    if (data.status === 'complete') return data;
    return await linkedge.pollJob(data.job_id);
  },
  downloadCarouselPdf: async (id, idx) => {
    const token = localStorage.getItem('le_token');
    const res = await fetch(`${API_BASE}/analyze/${id}/posts/${idx}/carousel/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Carousel PDF download failed');
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `carousel-post${idx + 1}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

export const adminApi = {
  listUsers: () => api.get('/admin/users').then((r) => r.data),
  createUser: (payload) => api.post('/admin/users', payload).then((r) => r.data),
  updateUser: (id, payload) => api.patch(`/admin/users/${id}`, payload).then((r) => r.data),
  listFeatureFlags: () => api.get('/admin/feature-flags').then((r) => r.data),
  getGrantLog: (params = {}) => api.get('/admin/grant-log', { params }).then((r) => r.data),
  listExpertPreferences: (params = {}) =>
    api.get('/admin/expert-preferences', { params }).then((r) => r.data),
  getExpertPreferences: (userId) =>
    api.get(`/admin/expert-preferences/${userId}`).then((r) => r.data),
  analyzeByUrl: (payload) => api.post('/admin/analyze-by-url', payload).then((r) => r.data),
  discover: (payload) => api.post('/admin/discover', payload).then((r) => r.data),
  discoveryRuns: (limit = 20) => api.get(`/admin/discover/runs?limit=${limit}`).then((r) => r.data),
  discoveryRun: (runId) => api.get(`/admin/discover/runs/${runId}`).then((r) => r.data),
  getAssignedUsers: (editorId) => api.get(`/admin/users/${editorId}/assigned`).then((r) => r.data),
  setAssignedUsers: (editorId, userIds) =>
    api.patch(`/admin/users/${editorId}/assigned`, { user_ids: userIds }).then((r) => r.data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`).then((r) => r.data),
  resetUserPassword: (id) =>
    api.post(`/admin/users/${id}/reset-password`).then((r) => r.data),
  listPasswordResetRequests: () =>
    api.get('/admin/password-reset-requests').then((r) => r.data),
  getWritingStyle: () => api.get('/admin/writing-style').then((r) => r.data),
  updateWritingStyle: (extra_banned_phrases) =>
    api
      .put('/admin/writing-style', { extra_banned_phrases })
      .then((r) => r.data),
  // --- Home-hero lead capture (LinkedIn URL + goal + plan intent) --------
  listHeroLeads: (params = {}) =>
    api.get('/admin/hero-leads', { params }).then((r) => r.data),
  // --- Free-Giveaway leads (/free landing page → concierge fulfilment) ---
  listFreeLeads: (params = {}) =>
    api.get('/admin/free-leads', { params }).then((r) => r.data),
  updateFreeLead: (id, payload) =>
    api.patch(`/admin/free-leads/${id}`, payload).then((r) => r.data),
  fulfilFreeLead: (id, { file, note = '' } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('note', note);
    return api.post(`/admin/free-leads/${id}/fulfil`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }).then((r) => r.data);
  },
  // --- Support inbox (Resend Inbound webhook → support_inbox) -----------
  listSupportInbox: (params = {}) =>
    api.get('/admin/support-inbox', { params }).then((r) => r.data),
  patchSupportInbox: (id, payload) =>
    api.patch(`/admin/support-inbox/${id}`, payload).then((r) => r.data),
  replySupportInbox: (id, payload) =>
    api.post(`/admin/support-inbox/${id}/reply`, payload).then((r) => r.data),
  // --- Support tickets ----------------------------------------------------
  listSupportTickets: (params = {}) =>
    api.get('/admin/support/tickets', { params }).then((r) => r.data),
  updateSupportTicket: (id, payload) =>
    api.patch(`/admin/support/tickets/${id}`, payload).then((r) => r.data),
  deleteSupportTicket: (id) =>
    api.delete(`/admin/support/tickets/${id}`).then((r) => r.data),
  // --- Tracking & Scripts (GA4, Meta Pixel, LinkedIn, custom JS) ----------
  listTrackingSnippets: () =>
    api.get('/admin/tracking-snippets').then((r) => r.data),
  createTrackingSnippet: (payload) =>
    api.post('/admin/tracking-snippets', payload).then((r) => r.data),
  updateTrackingSnippet: (id, payload) =>
    api.patch(`/admin/tracking-snippets/${id}`, payload).then((r) => r.data),
  deleteTrackingSnippet: (id) =>
    api.delete(`/admin/tracking-snippets/${id}`).then((r) => r.data),
  // --- Consent log (GDPR/UK PECR audit trail) -----------------------------
  listConsentLog: (params = {}) =>
    api.get('/admin/consent-log', { params }).then((r) => r.data),
  consentLogExportUrl: (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    )).toString();
    return `${API_BASE}/admin/consent-log/export.csv${qs ? `?${qs}` : ''}`;
  },
  // --- Inspirations control plane -----------------------------------------
  listAdminAnalyses: (params = {}) =>
    api.get('/admin/analyses', { params }).then((r) => r.data),
  listInspirationActivity: (params = {}) =>
    api.get('/admin/inspirations/activity', { params }).then((r) => r.data),
  addInspiration: ({ analysisId, leaderName, linkedinUrl, scrapeNow = true }) =>
    api
      .post('/admin/inspirations/add', {
        analysis_id: analysisId,
        leader_name: leaderName,
        linkedin_url: linkedinUrl,
        scrape_now: scrapeNow,
      }, { timeout: 300000 })
      .then((r) => r.data),
  rescrapeInspiration: ({ analysisId, slug }) =>
    api
      .post('/admin/inspirations/rescrape', { analysis_id: analysisId, slug }, { timeout: 300000 })
      .then((r) => r.data),
};

// Public (no-auth) endpoint used by the in-app TrackingInjector to fetch
// active snippets for the current surface (public landing pages vs the
// logged-in app). Inactive snippets are filtered server-side.
export const publicTrackingApi = {
  list: (surface = 'public') =>
    api.get(`/tracking/snippets?surface=${encodeURIComponent(surface)}`).then((r) => r.data),
};

// Consent logging — anonymous-friendly. Sends Authorization header if a
// token is present so logged-in events get tied to the user_id server-side.
export const consentApi = {
  log: ({ choice, clientId, pageUrl, consentVersion = '1.0' }) =>
    api
      .post('/consent/log', {
        choice,
        client_id: clientId,
        page_url: pageUrl,
        consent_version: consentVersion,
      })
      .then((r) => r.data),
};

export const authApi = {
  requestPasswordReset: (email) =>
    api.post('/auth/request-password-reset', { email }).then((r) => r.data),
};

// Expert-audit preference questionnaire (user-owned; upserted).
export const expertPrefsApi = {
  getMine: () => api.get('/expert-preferences').then((r) => r.data),
  save: (payload) => api.post('/expert-preferences', payload).then((r) => r.data),
};

export const downloadFile = async (analysisId, format) => {
  const token = localStorage.getItem('le_token');
  const res = await fetch(`${API_BASE}/analyze/${analysisId}/export/${format}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Download failed');
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `execpresenceai-report.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
export const downloadAdminAuditReport = async (userId, userLabel = 'user', tier = 'full', format = 'pdf') => {
  const safeTier = tier === 'partial' ? 'partial' : 'full';
  const safeFormat = format === 'pptx' ? 'pptx' : 'pdf';
  const token = localStorage.getItem('le_token');
  const res = await fetch(
    `${API_BASE}/admin/users/${userId}/audit-report?tier=${safeTier}&format=${safeFormat}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) {
    let detail = '';
    try { const j = await res.json(); detail = j?.detail || ''; } catch (_e) { /* ignore */ }
    throw new Error(detail || `Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safe = String(userLabel).replace(/[^a-z0-9-_]+/gi, '_').toLowerCase();
  const suffix = safeTier === 'partial' ? 'preview' : 'full';
  a.download = `execpresenceai-branded-audit-${safe}-${suffix}.${safeFormat}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Per-analysis branded audit download — used by the Dashboard cards so admins
// and editors can download the report for a SPECIFIC analysis (not just the
// user's latest one).
export const downloadAdminAuditReportByAnalysis = async (analysisId, label = 'audit',
                                                              tier = 'full', format = 'pdf') => {
  const safeTier = tier === 'partial' ? 'partial' : 'full';
  const safeFormat = format === 'pptx' ? 'pptx' : 'pdf';
  const token = localStorage.getItem('le_token');
  // Hard 3-minute client timeout. The branded report builder caps every
  // sub-call (Perplexity 45s, LLM top-up 20s, peer-positioning 25s) and
  // normal downloads complete in 5-20s — anything beyond 3 min means a
  // reverse-proxy or network issue, so we abort with a clear message
  // rather than letting the browser spin forever.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 180_000);
  let res;
  try {
    res = await fetch(
      `${API_BASE}/admin/analyses/${analysisId}/audit-report?tier=${safeTier}&format=${safeFormat}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal },
    );
  } catch (err) {
    clearTimeout(timer);
    if (err?.name === 'AbortError') {
      throw new Error(
        'Download took too long. Please try again in a moment — if it keeps timing out, use "Refresh research" from the dropdown and retry.'
      );
    }
    throw err;
  }
  clearTimeout(timer);
  if (!res.ok) {
    let detail = '';
    try { const j = await res.json(); detail = j?.detail || ''; } catch (_e) { /* ignore */ }
    throw new Error(detail || `Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safe = String(label).replace(/[^a-z0-9-_]+/gi, '_').toLowerCase();
  const suffix = safeTier === 'partial' ? 'preview' : 'full';
  a.download = `execpresenceai-branded-audit-${safe}-${suffix}.${safeFormat}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Per-free-lead branded audit download — looks up the lead's email → the
// user → their latest completed analysis on the backend, then streams the
// same PPTX/PDF as the per-user endpoint.
export const downloadFreeLeadAuditReport = async (leadId, label = 'lead',
                                                        tier = 'full', format = 'pptx') => {
  const safeTier = tier === 'partial' ? 'partial' : 'full';
  const safeFormat = format === 'pptx' ? 'pptx' : 'pdf';
  const token = localStorage.getItem('le_token');
  const res = await fetch(
    `${API_BASE}/admin/free-leads/${leadId}/audit-report?tier=${safeTier}&format=${safeFormat}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) {
    let detail = '';
    try { const j = await res.json(); detail = j?.detail?.message || j?.detail || ''; } catch (_e) { /* ignore */ }
    throw new Error(detail || `Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safe = String(label).replace(/[^a-z0-9-_]+/gi, '_').toLowerCase();
  const suffix = safeTier === 'partial' ? 'preview' : 'full';
  a.download = `blayzrr-lead-audit-${safe}-${suffix}.${safeFormat}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};



export const downloadAuditTimeline = async (format = 'csv', profileName = '') => {
  const fmt = format === 'pdf' ? 'pdf' : 'csv';
  const token = localStorage.getItem('le_token');
  const qs = new URLSearchParams({ format: fmt });
  if (profileName) qs.set('profile_name', profileName);
  qs.set('limit', '200');
  const res = await fetch(`${API_BASE}/analyze/timeline/export?${qs.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json();
      detail = j?.detail || '';
    } catch (_e) {
      // ignore
    }
    throw new Error(detail || `Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safe = (profileName || 'all').replace(/[^a-z0-9-_]+/gi, '_').toLowerCase();
  const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  a.download = `audit-timeline-${safe}-${stamp}.${fmt}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ============== AUTO-DESIGNED CAROUSEL ==============
export const generateAutoCarousel = async (analysisId, sourceType, sourceIdx, opts = {}) => {
  const body = {
    source_type: sourceType,
    source_idx: sourceIdx,
    regenerate: !!opts.regenerate,
    brand_label: opts.brandLabel || undefined,
    tone: opts.tone || undefined,
    density: opts.density || undefined,
    accent_color: opts.accentColor || undefined,
    card_count: typeof opts.cardCount === 'number' ? opts.cardCount : undefined,
    font: opts.font || undefined,
    // Phase 6 branding (deck-wide):
    logo_position: opts.logoPosition || undefined,
    background_image: opts.backgroundImage === undefined ? undefined : opts.backgroundImage,
    background_opacity:
      typeof opts.backgroundOpacity === 'number' ? opts.backgroundOpacity : undefined,
  };
  Object.keys(body).forEach((k) => body[k] === undefined && delete body[k]);
  const { data } = await api.post(`/analyze/${analysisId}/auto-carousel`, body);
  return data;
};

export const downloadAutoCarousel = async (analysisId, sourceType, sourceIdx) => {
  const token = localStorage.getItem('le_token');
  const qs = new URLSearchParams({
    source_type: sourceType,
    source_idx: String(sourceIdx),
  });
  const res = await fetch(
    `${API_BASE}/analyze/${analysisId}/auto-carousel/pdf?${qs.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) {
    let detail = '';
    try { const j = await res.json(); detail = j?.detail || ''; } catch (_e) {}
    throw new Error(detail || `Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `auto-carousel-${sourceType}-${sourceIdx}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Patch the cached carousel (design / brand_label / brand_logo / accent / font / branding)
export const updateAutoCarousel = async (analysisId, sourceType, sourceIdx, patch = {}) => {
  const body = {
    source_type: sourceType,
    source_idx: sourceIdx,
  };
  if (patch.design !== undefined)      body.design = patch.design;
  if (patch.brandLabel !== undefined)  body.brand_label = patch.brandLabel;
  if (patch.brandLogo !== undefined)   body.brand_logo = patch.brandLogo; // data URL or "" to clear
  if (patch.accentColor !== undefined) body.accent_color = patch.accentColor;
  if (patch.font !== undefined)        body.font = patch.font;
  if (patch.logoPosition !== undefined)      body.logo_position = patch.logoPosition;
  if (patch.backgroundImage !== undefined)   body.background_image = patch.backgroundImage; // "" clears
  if (patch.backgroundOpacity !== undefined) body.background_opacity = patch.backgroundOpacity;
  const { data } = await api.patch(`/analyze/${analysisId}/auto-carousel`, body);
  return data;
};

// --- Inspirations: pin / scrape / quota ----------------------------------
export const inspirations = {
  /** Return how many of the 5/month leader scrapes the current user has used. */
  quota: () => api.get('/inspirations/quota').then((r) => r.data),
  /** Pin a leader (by leader_id, name, and linkedin_url) onto an analysis. */
  pin: (analysisId, { leaderId, leaderName, linkedinUrl }) =>
    api
      .post(`/analyze/${analysisId}/inspirations/pin`, {
        leader_id: leaderId,
        leader_name: leaderName,
        linkedin_url: linkedinUrl,
      })
      .then((r) => r.data),
  /** Unpin a previously pinned leader. */
  unpin: (analysisId, leaderId) =>
    api
      .delete(`/analyze/${analysisId}/inspirations/pin/${encodeURIComponent(leaderId)}`)
      .then((r) => r.data),
  /** Scrape all pinned-but-not-yet-scraped leaders on the analysis.
   *  Long-running (Apify + LLM topic clustering); allow 5 minutes. */
  scrape: (analysisId) =>
    api
      .post(`/analyze/${analysisId}/inspirations/scrape`, {}, { timeout: 300000 })
      .then((r) => r.data),
  /** Admin-only: paste an arbitrary LinkedIn URL onto this analysis.
   *  Uses a SEPARATE 10-leader pool (independent of the user's 10-pin cap)
   *  and BYPASSES the user's 5/month scrape quota. */
  adminPin: (analysisId, { leaderName, linkedinUrl, scrapeNow = true }) =>
    api
      .post(
        `/analyze/${analysisId}/inspirations/admin-pin`,
        { leader_name: leaderName, linkedin_url: linkedinUrl, scrape_now: scrapeNow },
        { timeout: 300000 },
      )
      .then((r) => r.data),
  /** Admin-only: remove an admin-added custom pin. */
  adminUnpin: (analysisId, slug) =>
    api
      .delete(`/analyze/${analysisId}/inspirations/admin-pin/${encodeURIComponent(slug)}`)
      .then((r) => r.data),
  /** Admin-only: run Claude positioning analysis over SELECTED leaders.
   *  Long-running (Claude generates the structured report); allow 5 min. */
  generatePositioning: (analysisId, slugs) =>
    api
      .post(
        `/analyze/${analysisId}/inspirations/positioning`,
        { slugs },
        { timeout: 300000 },
      )
      .then((r) => r.data),
  /** Admin-only: download the positioning report as a multi-page A4 PDF
   *  (editorial / Big-4 style). Returns a Blob. */
  downloadPositioningPdf: (analysisId) =>
    api
      .get(`/analyze/${analysisId}/inspirations/positioning/pdf`, {
        responseType: 'blob',
        timeout: 180000,
      })
      .then((r) => r.data),
  /** Admin-only: download the positioning report as an editable .pptx. */
  downloadPositioningPptx: (analysisId) =>
    api
      .get(`/analyze/${analysisId}/inspirations/positioning/pptx`, {
        responseType: 'blob',
        timeout: 180000,
      })
      .then((r) => r.data),
};


// ============== STANDALONE CAROUSEL DESIGNER (arbitrary pasted text) ==============
// Generate a carousel design from arbitrary pasted text - no analysis_id needed.
export const standaloneCarouselDesign = async ({
  text,
  tone,
  density,
  accentColor,
  font,
  cardCount,
  brandLabel,
  stylePreset,
  mode,
} = {}) => {
  const body = {
    text: String(text || ''),
    tone: tone || undefined,
    density: density || undefined,
    accent_color: accentColor || undefined,
    font: font || undefined,
    card_count: typeof cardCount === 'number' && cardCount >= 6 && cardCount <= 10 ? cardCount : 0,
    brand_label: brandLabel || undefined,
    style_preset: stylePreset || undefined,
    mode: mode || undefined,
  };
  const { data } = await api.post('/carousel/standalone/design', body, { timeout: 120000 });
  return data;
};

// Render + download a PDF for a supplied design JSON (no DB lookup)
export const standaloneCarouselDownloadPdf = async ({
  design,
  brandLabel,
  brandLogo,
  accentColor,
  font,
  logoPosition,
  backgroundImage,
  backgroundOpacity,
  template,
  brandColor,
  filenameHint,
} = {}) => {
  const body = {
    design,
    brand_label: brandLabel || undefined,
    brand_logo: brandLogo === undefined ? undefined : brandLogo, // null/string allowed; undefined = leave default
    accent_color: accentColor || undefined,
    font: font || undefined,
    logo_position: logoPosition || undefined,
    background_image: backgroundImage === undefined ? undefined : backgroundImage,
    background_opacity:
      typeof backgroundOpacity === 'number' ? backgroundOpacity : undefined,
    template: template || undefined,
    brand_color: brandColor || undefined,
  };
  // Strip undefined to keep body clean
  Object.keys(body).forEach((k) => body[k] === undefined && delete body[k]);

  // Use axios (not fetch) so we share the auth header interceptor and so a
  // proxy-side idle-timeout surfaces a recognisable error in our existing
  // `getErrorMessage()` helper instead of an opaque "Failed to fetch".
  //
  // PDF rendering goes Playwright -> headless Chromium -> font load -> page
  // print, which can run 30-90s for a rich 7-card Designer deck. We allow
  // up to 3 minutes on the client; if the proxy in front of the backend
  // has a shorter idle timeout, that will still surface as ERR_NETWORK and
  // our error handler will tell the user clearly.
  const res = await api.post('/carousel/standalone/pdf', body, {
    responseType: 'blob',
    timeout: 180000,
  });

  const blob = res.data;
  // Defensive PDF integrity check. A correctly-rendered PDF always starts
  // with the magic bytes "%PDF-". If we ever get a 200 OK with a non-PDF
  // body (e.g. an error string sneaked through a streaming response, or a
  // CDN injected an HTML challenge page), saving it as a .pdf produces the
  // dreaded "We can't open this file" experience. Catch that here and
  // surface a useful error instead.
  try {
    const head = await blob.slice(0, 5).text();
    if (!head.startsWith('%PDF')) {
      const fullText = await blob.text();
      const snippet = fullText.slice(0, 240).replace(/\s+/g, ' ').trim();
      throw new Error(
        `Server returned ${blob.size} bytes but the body is not a PDF. ` +
        `First chars: "${snippet || '(empty)'}". Please retry; if it keeps ` +
        'happening, the backend renderer failed silently.',
      );
    }
  } catch (sniffErr) {
    // If our magic-bytes check threw because the response is bad, propagate.
    // If it threw because blob.text() itself failed, still propagate - we
    // do NOT want to save a corrupted file.
    if (sniffErr?.message?.includes('not a PDF')) throw sniffErr;
    throw new Error('PDF integrity check failed before save - the download was corrupted in transit.');
  }

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safe = String(filenameHint || 'carousel-designer')
    .replace(/[^a-z0-9-_]+/gi, '-')
    .slice(0, 40);
  a.download = `${safe || 'carousel-designer'}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};


// User-level brand profile (label + logo persistence)
export const getMyBrand = async () => {
  const { data } = await api.get('/users/me/brand');
  return data; // { brand_label, brand_logo }
};

export const setMyBrand = async ({ brandLabel, brandLogo } = {}) => {
  const body = {};
  if (brandLabel !== undefined) body.brand_label = brandLabel;
  if (brandLogo !== undefined)  body.brand_logo = brandLogo; // "" to clear
  const { data } = await api.put('/users/me/brand', body);
  return data;
};




// Change the logged-in user's own password. Backend enforces:
//   - current_password must match
//   - new_password >= 6 chars
//   - new_password != current_password
export const changePassword = async ({ currentPassword, newPassword } = {}) => {
  const { data } = await api.post('/auth/change-password', {
    current_password: String(currentPassword || ''),
    new_password: String(newPassword || ''),
  });
  return data; // { ok: true }
};
